import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { AlertCircle, ArrowRight, Eye, EyeOff, Phone } from 'lucide-react';
import { useAuthStore } from '@store/authStore';

// ─────────────────────────────────────────────────────────────────────────────
// Validation schema
// ─────────────────────────────────────────────────────────────────────────────

const loginSchema = z.object({
  identifier: z.string().min(1, 'Enter your phone number or email.'),
  password: z.string().min(6, 'Password must be at least 6 characters.'),
});

type LoginFormData = z.infer<typeof loginSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// LoginPage
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Admin login. A full-bleed, single-purpose screen, so it is one of the few
 * places the aurora is allowed (§4: heroes, final CTA and coming-soon only,
 * never as page texture). The mint ground and Bricolage headline come from the
 * design system; the old warm-cream + Fraunces editorial treatment is gone.
 */
export const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const currentUser = useAuthStore((state) => state.user);
  const storeLoading = useAuthStore((state) => state.isLoading);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: '', password: '' },
  });

  const isLoading = isSubmitting || storeLoading;

  // Already authenticated (or just logged in) → dashboard. Reactive so it only
  // fires once the store is actually set; ProtectedRoute never sees a half-set
  // state that would bounce back to /login.
  if (isAuthenticated && currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    setAuthError(null);
    try {
      await login(data);
      // Do NOT navigate imperatively — the reactive redirect below fires the
      // moment the store flips isAuthenticated, so we never route before the
      // session (and the token the API layer reads) is committed.
      toast.success('Welcome back.');
    } catch (error: unknown) {
      const raw = (error as { message?: string })?.message ?? 'Invalid phone or password.';
      const message = raw.length > 120 ? 'Authentication failed. Please check your credentials and try again.' : raw;
      setAuthError(message);
      toast.error(message);
    }
  };

  return (
    // A full-bleed, single-purpose screen, so it carries the aurora (§4: heroes,
    // final CTA and single-purpose screens only, never as page texture on a
    // content page). The orbs live on this wrapper, not on the form column:
    // `.aurora-bg` sets overflow:hidden, so anything it is applied to clips them.
    //
    // Noise sits on its own child. `.noise-overlay` also paints into ::after and
    // would fight `.aurora-bg` over the same pseudo-element; that bug dragged an
    // orb to the top-left corner in Customer-Portal.
    <div className="aurora-bg relative min-h-screen overflow-hidden bg-[var(--bg-base)] flex flex-col">
      <span
        className="noise-overlay pointer-events-none absolute inset-0 z-0"
        aria-hidden="true"
      />
      <div
        className="aurora-orb-accent w-80 h-80 md:w-96 md:h-96 top-1/3 right-1/4"
        aria-hidden="true"
      />

      {/* ── Header ───────────────────────────────────────────────────────── */}
      {/* The mark is centred on the page axis so it sits directly above the
          440px form column rather than off in the left margin. The old comment
          here claimed no logo asset existed; public/chekit_logo.png does. */}
      <header className="relative z-10 w-full px-6 pt-10 pb-2">
        <div className="flex justify-center">
          {/* Theme-aware: the forest mark is ~1.7:1 on the dark base (invisible),
              so the mint variant shows in dark. Tailwind dark: is keyed on
              [data-theme="dark"] here. */}
          <img
            src="/chekit-logo.png"
            alt="Chekit"
            className="h-20 w-20 md:h-28 md:w-28 object-contain block dark:hidden"
          />
          <img
            src="/chekit-logo-dark.png"
            alt=""
            aria-hidden="true"
            className="h-20 w-20 md:h-28 md:w-28 object-contain hidden dark:block"
          />
        </div>
      </header>

      {/* ── Main ─────────────────────────────────────────────────────────── */}
      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-[440px]">

          {/* Eyebrow */}
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--text-muted)]">
            Admin Portal
          </p>

          {/* Headline: Bricolage display */}
          <h1 className="font-display mt-3 text-[40px] sm:text-[48px] md:text-[56px] font-medium leading-[1.02] tracking-[-0.03em] text-[var(--text-primary)]">
            Sign in to{' '}
            <span className="gradient-text">Chekit</span>
            {'.'}
          </h1>

          {/* Subtitle */}
          <p className="text-[15px] text-[var(--text-secondary)] leading-[1.55] mt-3 mb-8">
            Internal access only. Your session is monitored for security.
          </p>

          {/* Error banner: only rendered when there is an auth-level error */}
          {authError !== null && (
            <div
              role="alert"
              className="bg-[var(--state-danger-bg)] border border-[var(--state-danger-border)] text-[var(--state-danger)] rounded-2xl p-4 mb-5 flex items-start gap-3 text-[13px] font-medium leading-[1.5]"
            >
              <AlertCircle
                size={16}
                strokeWidth={1.75}
                className="mt-0.5 shrink-0"
                aria-hidden="true"
              />
              <span>{authError}</span>
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col gap-5"
          >
            {/* ── Phone or Email ─────────────────────────────────────────── */}
            <div>
              <label
                htmlFor="identifier"
                className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2 block"
              >
                Phone or Email
              </label>
              <div className="relative">
                <Phone
                  size={16}
                  strokeWidth={1.75}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)] pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  {...register('identifier')}
                  id="identifier"
                  type="text"
                  autoComplete="username"
                  placeholder="+254700000001"
                  disabled={isLoading}
                  onBlur={() => setPhoneTouched(true)}
                  className={[
                    'w-full rounded-2xl border bg-white pl-10 pr-4 py-3.5',
                    'text-[15px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
                    ' transition-colors duration-200',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    errors.identifier && phoneTouched
                      ? 'border-[var(--state-danger)] focus:border-[var(--state-danger)]'
                      : 'border-[var(--border-default)] focus:border-[var(--border-strong)]',
                  ].join(' ')}
                />
              </div>
              {errors.identifier && phoneTouched && (
                <p className="text-[12px] text-[var(--state-danger)] mt-1.5">
                  {errors.identifier.message}
                </p>
              )}
            </div>

            {/* ── Password ───────────────────────────────────────────────── */}
            <div>
              <label
                htmlFor="password"
                className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-2 block"
              >
                Password
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Your password"
                  disabled={isLoading}
                  className={[
                    'w-full rounded-2xl border bg-white px-4 py-3.5 pr-12',
                    'text-[15px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]',
                    ' transition-colors duration-200',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    errors.password
                      ? 'border-[var(--state-danger)] focus:border-[var(--state-danger)]'
                      : 'border-[var(--border-default)] focus:border-[var(--border-strong)]',
                  ].join(' ')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-200"
                >
                  {showPassword
                    ? <EyeOff size={16} strokeWidth={1.75} aria-hidden="true" />
                    : <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
                  }
                </button>
              </div>
              {errors.password && (
                <p className="text-[12px] text-[var(--state-danger)] mt-1.5">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* ── Forgot password ────────────────────────────────────────── */}
            <div className="flex justify-end mt-1">
              <a
                href="#forgot"
                className="text-[13px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-200"
              >
                Forgot password?
              </a>
            </div>

            {/* ── Submit ─────────────────────────────────────────────────── */}
 {/* §7.1, THE BUTTON NEVER MOVES. This had ``,
                which lifts the button body on hover. The canonical interaction is
                exactly three things: brighten, cast a green glow, and settle on
                press. No lift, no scale, no transform on the button itself. The
                icon inside may nudge; the button may not. */}
            <button
              type="submit"
              disabled={isLoading}
              className={[
                'group w-full rounded-full gradient-bg px-7 py-4 mt-2',
                'text-[15px] font-semibold text-white',
                'inline-flex items-center justify-center gap-2',
                'transition-all duration-200 ease-out',
                'hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)]',
                'active:brightness-95',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              ].join(' ')}
            >
              {isLoading ? (
                <>
                  {/* Lightweight CSS spinner: no extra dependency */}
                  <span
                    aria-hidden="true"
                    className="inline-block w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"
                  />
                  <span>Signing in&hellip;</span>
                </>
              ) : (
                <>
                  <span>Sign in</span>
                  <ArrowRight
                    size={16}
                    strokeWidth={1.75}
                    aria-hidden="true"
                    className="transition-transform duration-300 group-hover:translate-x-0.5"
                  />
                </>
              )}
            </button>
          </form>
        </div>
      </main>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="border-t border-[var(--border-subtle)] py-6">
        <div className="max-w-[1200px] mx-auto px-6 flex items-center justify-between">
          <p className="text-[12px] text-[var(--text-muted)]">
            &copy; 2026 Chekit. All rights reserved.
          </p>
          <nav className="flex items-center gap-4" aria-label="Footer links">
            <a
              href="#terms"
              className="text-[12px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-200"
            >
              Terms
            </a>
            <span className="text-[var(--text-muted)] text-[12px]" aria-hidden="true">
              &middot;
            </span>
            <a
              href="#privacy"
              className="text-[12px] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors duration-200"
            >
              Privacy
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};